import { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, ImageIcon, Check, Maximize2 } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../../lib/axios";
import { DOWNLOAD_BASE_URL } from "../../../lib/api";

// Stored image URLs are baked at upload time and can point at a stale/localhost
// host (e.g. when PUBLIC_API_URL isn't set) → broken thumbnails. Rewrite any
// stored file URL onto the current API base so it always resolves.
const resolveImg = (u?: string) => {
  if (!u) return "";
  const i = u.indexOf("/api/files/");
  if (i >= 0) return `${DOWNLOAD_BASE_URL}${u.slice(i)}`;
  const j = u.indexOf("/uploads/");
  if (j >= 0) return `${DOWNLOAD_BASE_URL}${u.slice(j)}`;
  return u.startsWith("http") ? u : `${DOWNLOAD_BASE_URL}${u.startsWith("/") ? "" : "/"}${u}`;
};

export interface PortfolioAsset {
  _id: string;
  name: string;
  url: string;
  size?: number;
  createdAt?: string;
}

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = reject;
    r.readAsDataURL(file);
  });

// File Storage picker for proposal portfolio samples: reuse previously stored
// samples or upload new images (stored in the Files module for future use).
export default function PortfolioPickerModal({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (assets: PortfolioAsset[]) => void;
}) {
  const [assets, setAssets] = useState<PortfolioAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [preview, setPreview] = useState<PortfolioAsset | null>(null);
  // Import a portfolio image from a public URL — stored internally, reusable.
  const [urlDraft, setUrlDraft] = useState("");
  const [importing, setImporting] = useState(false);
  const importFromUrl = async () => {
    const u = urlDraft.trim();
    if (!u) return;
    setImporting(true);
    try {
      const { data } = await axiosInstance.post("/proposals/portfolio-assets/from-url", { url: u });
      setAssets((prev) => [data.asset, ...prev]);
      setSelected((s) => ({ ...s, [data.asset._id]: true }));
      setUrlDraft("");
    } catch (e) {
      alert((e as any)?.response?.data?.message || "Couldn't import that URL");
    } finally {
      setImporting(false);
    }
  };
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      const { data } = await axiosInstance.get("/proposals/portfolio-assets");
      setAssets(data.assets || []);
    } catch {
      toast.error("Couldn't load your portfolio samples");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); continue; }
        const data = await readAsDataUrl(f);
        const res = await axiosInstance.post("/proposals/portfolio-assets", { data, fileName: f.name, fileSize: f.size });
        setAssets((a) => [res.data.asset, ...a]);
        setSelected((s) => ({ ...s, [res.data.asset._id]: true }));
      }
      toast.success("Uploaded — stored in your Files module for future proposals.");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const chosen = assets.filter((a) => selected[a._id]);

  return (
    <>
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-lg">Portfolio Samples</h3>
            <p className="text-xs text-gray-500">Pick from your Portfolio library, import a public URL, or upload new images</p>
            <div className="flex gap-1.5 mt-2">
              <input
                value={urlDraft}
                onChange={(e) => setUrlDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); importFromUrl(); } }}
                placeholder="Paste a public portfolio image URL — it's downloaded into your library"
                className="flex-1 px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
              />
              <button
                type="button"
                onClick={importFromUrl}
                disabled={importing || !urlDraft.trim()}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white disabled:opacity-50"
              >
                {importing ? "Importing…" : "Import URL"}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60 text-sm font-medium"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Upload images
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="h-40 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
          ) : assets.length === 0 ? (
            <div className="h-40 grid place-items-center text-center text-gray-500">
              <div>
                <ImageIcon className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                <p className="text-sm font-medium">No portfolio samples yet</p>
                <p className="text-xs">Upload images — they're stored in your Files module and reusable in future proposals.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {assets.map((a) => (
                <div
                  key={a._id}
                  onClick={() => setSelected((s) => ({ ...s, [a._id]: !s[a._id] }))}
                  className={`group relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${selected[a._id] ? "border-emerald-500 ring-2 ring-emerald-200" : "border-gray-200 hover:border-gray-300"}`}
                >
                  <div className="w-full h-28 bg-gray-100 grid place-items-center overflow-hidden">
                    <img
                      src={resolveImg(a.url)}
                      alt={a.name}
                      loading="lazy"
                      className="w-full h-28 object-cover"
                      onError={(e) => {
                        const img = e.currentTarget;
                        img.style.display = "none";
                        img.parentElement?.classList.add("broken-img");
                      }}
                    />
                    <ImageIcon className="w-7 h-7 text-gray-300 hidden [.broken-img_&]:block" />
                  </div>
                  <p className="text-[11px] text-gray-600 truncate px-2 py-1 bg-white text-left">{a.name}</p>

                  {/* Enlarge / view */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setPreview(a); }}
                    title="Enlarge"
                    className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full bg-black/55 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/75"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </button>

                  {selected[a._id] && (
                    <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-500 text-white grid place-items-center">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <span className="text-sm text-gray-500">{chosen.length} selected</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm">Cancel</button>
            <button
              onClick={() => { if (!chosen.length) { toast.error("Select at least one sample"); return; } onPick(chosen); onClose(); }}
              className="px-5 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 text-sm font-medium"
            >
              Attach to proposal
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Enlarge lightbox */}
    {preview && (
      <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
        <button
          onClick={() => setPreview(null)}
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>
        <figure className="max-w-[90vw] max-h-[88vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
          <img src={resolveImg(preview.url)} alt={preview.name} className="max-w-full max-h-[82vh] object-contain rounded-lg shadow-2xl" />
          <figcaption className="text-white/80 text-sm">{preview.name}</figcaption>
        </figure>
      </div>
    )}
    </>
  );
}
